import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';

import { appWithTranslation } from 'next-i18next';
import { DefaultSeo } from 'next-seo';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Router from 'next/router';
import Script from 'next/script';

// eslint-disable-next-line import-helpers/order-imports
import { ChakraProvider, ColorModeScript } from '@chakra-ui/react';

import 'react-toastify/dist/ReactToastify.css';

import GlobalStyle from 'styles/global';
import theme from 'styles/themes/default';

import * as chakrauiTheme from 'config/chackraui-color-theme';
import SEO from 'config/seo';

import 'lib/yup';

const AppProvider = dynamic(() => import('hooks'), { ssr: false });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

Router.events.on('routeChangeStart', () => {
  const { body } = document;
  body.classList.add('loading-page');
});
Router.events.on('routeChangeComplete', () => {
  const { body } = document;
  body.classList.remove('loading-page');
});
Router.events.on('routeChangeError', () => {
  const { body } = document;
  body.classList.remove('loading-page');
});

const MyApp = ({ Component, pageProps }: AppProps): JSX.Element => (
  <>
    <Head>
      <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />

      <meta name="HandheldFriendly" content="True" />
      <meta name="MobileOptimized" content="320" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1.0, maximum-scale=1, user-scalable=no"
      />
      <meta httpEquiv="Cache-Control" content="no-cache, no-store, must-revalidate" />

      <link rel="icon" type="image/png" href="/images/logo/favicon.png" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" />
    </Head>

    <DefaultSeo {...SEO} />

    <ToastContainer />

    <ChakraProvider resetCSS={false} theme={chakrauiTheme.theme}>
      <QueryClientProvider client={queryClient}>
        <AppProvider>
          <Component {...pageProps} />
        </AppProvider>
      </QueryClientProvider>
    </ChakraProvider>

    <GlobalStyle theme={theme} />

    <ColorModeScript initialColorMode={chakrauiTheme.config.initialColorMode} />

    {process.env.NEXT_PUBLIC_ENV === 'production' && (
      <>
        <Script
          defer
          id="clarity-script"
          dangerouslySetInnerHTML={{
            __html: `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "PENDENTE")`,
          }}
        />

        <Script async src="https://www.googletagmanager.com/gtag/js?id=PENDENTE" defer />

        <Script
          defer
          id="google-analytics"
          dangerouslySetInnerHTML={{
            __html: `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'PENDENTE');`,
          }}
        />
      </>
    )}
  </>
);

export default appWithTranslation(MyApp);
